import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, TrashIcon, RectangleStackIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { fetchCarouselItems, createCarouselItem, updateCarouselItem, deleteCarouselItem } from '../../store/slices/carouselSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Button, Badge, Pagination, ConfirmDialog, Card } from '../../components/Common';
import CarouselModal from '../../components/Modals/CarouselModal';
import api from '../../services/api';

const CarouselList = () => {
  const dispatch = useDispatch();
  const { carouselItems, loading, pagination } = useSelector((state) => state.carousel);
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [draggedItem, setDraggedItem] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    dispatch(fetchCarouselItems({ page, size: pageSize }));
  }, [dispatch, page, pageSize]);

  // Update local items when Redux state changes
  useEffect(() => {
    setItems([...carouselItems]);
  }, [carouselItems]);

  const openModal = (item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await dispatch(updateCarouselItem({ id: editingItem.id, data: formData })).unwrap();
        dispatch(showSuccess('Carousel item updated successfully'));
      } else {
        await dispatch(createCarouselItem(formData)).unwrap();
        dispatch(showSuccess('Carousel item created successfully'));
      }
      closeModal();
      dispatch(fetchCarouselItems({ page, size: pageSize }));
    } catch (error) {
      console.error('Submit error:', error);
      dispatch(showError(error || 'Failed to save carousel item'));
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteCarouselItem(deleteModal.id)).unwrap();
        dispatch(showSuccess('Carousel item deleted successfully'));
        dispatch(fetchCarouselItems({ page, size: pageSize }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete carousel item'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, index) => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.index === targetIndex) {
      return;
    }

    try {
      // Fetch all carousel items so reorder works across pages
      const allResponse = await api.get('/carousel/admin/all', { params: { size: 1000 } });
      let allItems = allResponse.data?.data || [];

      // Find dragged item and target in the full list by id
      const draggedFullIndex = allItems.findIndex(item => item.id === draggedItem.item.id);
      const targetFullIndex = allItems.findIndex(item => item.id === items[targetIndex].id);

      if (draggedFullIndex === -1 || targetFullIndex === -1) {
        throw new Error('Could not find item in full list');
      }

      // Reorder the full list
      const [removed] = allItems.splice(draggedFullIndex, 1);
      allItems.splice(targetFullIndex, 0, removed);

      // Update local display for current page
      setItems([...allItems.slice((page - 1) * pageSize, page * pageSize)]);

      // Save new order to backend
      await api.post('/carousel/admin/reorder', {
        items: allItems.map((item, index) => ({
          id: item.id,
          display_order: index
        }))
      });
      dispatch(showSuccess('Carousel order updated successfully'));
      dispatch(fetchCarouselItems({ page, size: pageSize }));
    } catch (error) {
      console.error('Failed to update order:', error);
      dispatch(showError('Failed to update carousel order'));
      // Revert on error
      setItems([...carouselItems]);
    }
  };


  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Carousel</h1>
          <p className="text-slate-500">Manage homepage carousel slides - Drag to reorder</p>
        </div>
        <Button icon={PlusIcon} onClick={() => openModal()}>New Slide</Button>
      </div>

      <Card noPadding>
        {/* Custom draggable table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Carousel Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Tag
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    <RectangleStackIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No carousel items found</p>
                  </td>
                </tr>
              ) : (
                items.map((row, index) => (
                  <tr
                    key={row.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, row, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    className="hover:bg-slate-50 cursor-move transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <ArrowsUpDownIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        {row.image_url || row.coverImageUrl ? (
                          <img src={row.image_url || row.coverImageUrl} alt="" className="w-20 h-12 rounded-lg object-cover" />
                        ) : (
                          <div className="w-20 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <RectangleStackIcon className="w-6 h-6 text-indigo-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-slate-800 line-clamp-1">{row.title || 'Untitled'}</p>
                          <p className="text-sm text-slate-500 line-clamp-1">{row.subtitle || 'No subtitle'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-slate-600">{index + 1}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="primary">{row.tag || 'general'}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={row.status?.name === 'Active' || row.status?.id === 1 ? 'success' : 'warning'} dot>
                        {row.status?.name || 'Active'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openModal(row)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit">
                          <PencilIcon className="w-4 h-4 text-slate-500" />
                        </button>
                        <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* Carousel Modal */}
      <CarouselModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        carousel={editingItem}
      />

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete Carousel Item" message="Are you sure you want to delete this carousel item?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default CarouselList;
